// src/notifications/registerPushNotifications.js
//
// Registers this device for push notifications and stores the FCM token in Firestore.
//
// Firestore structure used by functions/index.js:
//   users/{uid}/fcmTokens/{safeTokenId}
//   users/{uid}/notificationSettings/main

import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";

import { collection, doc, getDocs, query, setDoc, serverTimestamp, where, writeBatch, runTransaction } from "firebase/firestore";
import { getToken as getWebFcmToken, deleteToken as deleteWebFcmToken } from "firebase/messaging";

import { auth, db, messaging, messagingReady, VAPID_KEY } from "../firebase";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { getCurrentEffectiveSession, isEffectiveSessionLoggedIn } from "../auth/effectiveSession";
import { openLoopNotificationRoute, buildLoopNotificationRoute, getLoopNotificationData } from "./notificationRouting";

const APP_NAME = "loop-reservas";

let registrationInProgress = false;
let lastRegisteredUid = null;
let listenersAttached = false;
let registrationEpoch = 0;
let signingOut = false;

function getPlatform() {
  try {
    if (typeof Capacitor !== "undefined" && Capacitor?.getPlatform) {
      return Capacitor.getPlatform() || "web";
    }

    if (typeof window === "undefined") return "web";
    const cap = window.Capacitor || null;
    if (!cap) return "web";

    const platform =
      typeof cap.getPlatform === "function"
        ? cap.getPlatform()
        : cap.platform || "web";

    return platform || "web";
  } catch {
    return "web";
  }
}

function isNativePlatform(platform) {
  return platform === "ios" || platform === "android";
}

function getEffectiveUser() {
  try {
    const session = getCurrentEffectiveSession({ firebaseAuth: auth });
    if (isEffectiveSessionLoggedIn(session)) {
      return {
        uid: String(session.user.uid || ""),
        email: session.user.email || "",
        displayName: session.user.displayName || "",
        source: session.source || "effective-session",
      };
    }
  } catch (_) {}

  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && ma.uid) {
      return {
        uid: String(ma.uid),
        email: ma.email || "",
        displayName: ma.displayName || "",
        source: ma.source || "manual-auth",
      };
    }
  } catch (_) {}

  return null;
}

function safeTokenDocId(token) {
  return String(token || "")
    .replace(/\//g, "_")
    .replace(/\./g, "_")
    .replace(/#/g, "_")
    .replace(/\[/g, "_")
    .replace(/\]/g, "_")
    .replace(/\s/g, "")
    .slice(0, 450);
}

async function saveTokenForUser({ user, token, platform, provider }) {
  if (!user?.uid || !token) {
    return { ok: false, reason: "missing-user-or-token" };
  }

  if (signingOut || getEffectiveUser()?.uid !== user.uid) return {ok:false,reason:"session-changed"};
  const tokenDocId = safeTokenDocId(token);

  await setDoc(
    doc(db, "users", user.uid, "fcmTokens", tokenDocId),
    {
      token,
      platform,
      provider,
      app: APP_NAME,
      enabled: true,
      userId: user.uid,
      userEmail: user.email || null,
      userDisplayName: user.displayName || null,
      userAuthSource: user.source || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    },
    { merge: true }
  );

  const settingsRef = doc(db, 'users', user.uid, 'notificationSettings', 'main');
  await runTransaction(db, async transaction => {
    const existing = await transaction.get(settingsRef);
    if (!existing.exists()) transaction.set(settingsRef, {pushEnabled:true, createdAt:serverTimestamp(), updatedAt:serverTimestamp()});
  });
  try {localStorage.setItem('loop_push_registration', JSON.stringify({uid:user.uid,token,platform}));} catch (_) {}

  return { ok: true, token, platform, provider };
}

async function cleanupLegacyIosApnsTokensForUser(user) {
  try {
    if (!user?.uid) return;

    const legacyQuery = query(
      collection(db, "users", user.uid, "fcmTokens"),
      where("platform", "==", "ios"),
      where("provider", "==", "capacitor-push-notifications")
    );

    const snap = await getDocs(legacyQuery);
    if (!snap || snap.empty) return;

    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();

    console.log("[Loop Push] Removed legacy iOS APNs tokens saved as FCM tokens", snap.size);
  } catch (error) {
    console.warn("[Loop Push] cleanupLegacyIosApnsTokensForUser failed", error);
  }
}

function routeNativePushAction(event, source = "native") {
  try {
    const data = getLoopNotificationData(event);
    const route = buildLoopNotificationRoute(data);

    try {
      window.__LOOP_PUSH_LAST_NOTIFICATION_ACTION__ = {
        source,
        route,
        data,
        event,
        time: new Date().toISOString(),
      };
    } catch (_) {}

    openLoopNotificationRoute(data);
  } catch (e) {
    console.warn("[Loop Push] routeNativePushAction failed", e);
  }
}

function attachNativeListenersOnce() {
  if (listenersAttached) return;
  listenersAttached = true;
  FirebaseMessaging.addListener?.("tokenReceived", async event => {
    const user=getEffectiveUser();
    if (!signingOut && user?.uid && event.token) {
      try {await saveTokenForUser({user,token:event.token,platform:getPlatform(),provider:"capacitor-firebase-messaging"});} catch (error) {console.warn("Push token refresh failed",error);}
    }
  }).catch?.(error => console.warn("Push token listener failed",error));

  try {
    FirebaseMessaging.addListener?.("notificationReceived", (event) => {
      console.log("[Loop Push] notificationReceived", event);
      try {
        window.__LOOP_PUSH_LAST_NOTIFICATION__ = {
          type: "received",
          event,
          data: getLoopNotificationData(event),
          time: new Date().toISOString(),
        };
      } catch (_) {}
    });

    FirebaseMessaging.addListener?.("notificationActionPerformed", (event) => {
      console.log("[Loop Push] notificationActionPerformed", event);
      routeNativePushAction(event, "capacitor-firebase-messaging");
    });
  } catch (e) {
    console.warn("[Loop Push] FirebaseMessaging listeners failed", e);
  }

  try {
    PushNotifications.addListener?.("pushNotificationReceived", (notification) => {
      console.log("[Loop Push] pushNotificationReceived", notification);
      try {
        window.__LOOP_PUSH_LAST_NOTIFICATION__ = {
          type: "received",
          event: notification,
          data: getLoopNotificationData(notification),
          time: new Date().toISOString(),
        };
      } catch (_) {}
    });

    PushNotifications.addListener?.("pushNotificationActionPerformed", (event) => {
      console.log("[Loop Push] pushNotificationActionPerformed", event);
      routeNativePushAction(event, "capacitor-push-notifications");
    });
  } catch (e) {
    console.warn("[Loop Push] PushNotifications listeners failed", e);
  }
}

async function requestNativeFcmToken(platform) {
  attachNativeListenersOnce();

  // Preferred and only valid token for your Firebase Admin backend:
  // @capacitor-firebase/messaging returns an FCM token.
  try {
    const supported = await FirebaseMessaging.isSupported?.();
    if (supported && supported.isSupported === false) {
      console.warn("[Loop Push] FirebaseMessaging unsupported", supported);
    } else {
      const currentPerm = await FirebaseMessaging.checkPermissions?.();
      let receive = currentPerm?.receive;

      if (receive !== "granted") {
        const requested = await FirebaseMessaging.requestPermissions?.();
        receive = requested?.receive;
      }

      if (receive !== "granted") {
        return { ok: false, reason: "permission-denied", platform };
      }

      // iOS needs APNs registration before Firebase can reliably issue an FCM token.
      // AppDelegate.swift maps APNs -> FirebaseMessaging.apnsToken.
      if (platform === "ios") {
        try {
          await PushNotifications.register?.();
        } catch (e) {
          console.warn("[Loop Push] iOS APNs register warmup failed", e);
        }

        // Give the native bridge a moment to receive APNs and map it to Firebase.
        await new Promise((resolve) => setTimeout(resolve, 650));
      }

      const result = await FirebaseMessaging.getToken();
      const token = result?.token;

      if (token) {
        return { ok: true, token, provider: "capacitor-firebase-messaging" };
      }

      console.warn("[Loop Push] FirebaseMessaging returned empty token", result);
    }
  } catch (error) {
    console.warn("[Loop Push] FirebaseMessaging token failed", error);
  }

  // Android fallback: @capacitor/push-notifications returns an FCM token on Android.
  // Do NOT use this fallback on iOS because it returns an APNs token, not an FCM token.
  if (platform === "ios") {
    return {
      ok: false,
      reason: "ios-fcm-token-unavailable",
      platform,
      hint: "Do not save APNs tokens in users/{uid}/fcmTokens. Check APNs key in Firebase and AppDelegate APNs mapping.",
    };
  }

  try {
    let perm = await PushNotifications.checkPermissions?.();

    if (perm?.receive !== "granted") {
      perm = await PushNotifications.requestPermissions?.();
    }

    if (perm?.receive !== "granted") {
      return { ok: false, reason: "permission-denied", platform };
    }

    const tokenResult = await new Promise((resolve) => {
      let done = false;

      const finish = (value) => {
        if (done) return;
        done = true;
        resolve(value);
      };

      PushNotifications.addListener?.("registration", (token) => {
        finish({
          ok: true,
          token: token?.value || "",
          provider: "capacitor-push-notifications",
        });
      });

      PushNotifications.addListener?.("registrationError", (error) => {
        finish({ ok: false, reason: "registration-error", error });
      });

      setTimeout(() => {
        finish({ ok: false, reason: "registration-timeout" });
      }, 10000);

      PushNotifications.register?.();
    });

    if (!tokenResult?.ok || !tokenResult.token) return tokenResult;
    return tokenResult;
  } catch (error) {
    console.warn("[Loop Push] PushNotifications fallback failed", error);
    return { ok: false, reason: "push-notifications-fallback-failed", error, platform };
  }
}

async function requestWebFcmToken() {
  await messagingReady;
  if (!messaging) return { ok: false, reason: "web-messaging-not-available" };
  if (typeof Notification === "undefined") {
    return { ok: false, reason: "notifications-api-not-available" };
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();

  if (permission !== "granted") {
    return { ok: false, reason: "permission-denied" };
  }

  if (!VAPID_KEY) {
    return {
      ok: false,
      reason: "missing-vapid-key",
      hint: "Set REACT_APP_FIREBASE_VAPID_KEY for web push.",
    };
  }

  try {
    const token = await getWebFcmToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return { ok: false, reason: "empty-web-token" };
    return { ok: true, token, provider: "firebase-web-messaging" };
  } catch (error) {
    console.warn("[Loop Push] web FCM token failed", error);
    return { ok: false, reason: "web-token-failed", error };
  }
}

export async function registerPushNotifications({ force = false } = {}) {
  const user = getEffectiveUser();

  if (!user?.uid) {
    return { ok: false, reason: "no-user" };
  }

  if (registrationInProgress) {
    return { ok: false, reason: "registration-in-progress" };
  }

  if (!force && lastRegisteredUid === user.uid) {
    return { ok: true, reason: "already-registered-for-user" };
  }

  signingOut = false;
  const epoch = registrationEpoch;
  registrationInProgress = true;

  try {
    const platform = getPlatform();

    const tokenResult = isNativePlatform(platform)
      ? await requestNativeFcmToken(platform)
      : await requestWebFcmToken();

    if (!tokenResult?.ok || !tokenResult.token) {
      try {
        window.__LOOP_PUSH_LAST_REGISTER__ = {
          ok: false,
          uid: user.uid,
          platform,
          reason: tokenResult?.reason || "token-result-empty",
          time: new Date().toISOString(),
        };
      } catch (_) {}

      return tokenResult || { ok: false, reason: "token-result-empty" };
    }

    if (epoch !== registrationEpoch || getEffectiveUser()?.uid !== user.uid) return {ok:false,reason:"session-changed"};
    const saveResult = await saveTokenForUser({
      user,
      token: tokenResult.token,
      platform,
      provider: tokenResult.provider,
    });

    if (saveResult.ok && platform === "ios") {
      await cleanupLegacyIosApnsTokensForUser(user);
    }

    if (saveResult.ok) {
      lastRegisteredUid = user.uid;

      try {
        window.__LOOP_PUSH_LAST_REGISTER__ = {
          ok: true,
          uid: user.uid,
          platform,
          provider: tokenResult.provider,
          tokenPreview: `${String(tokenResult.token).slice(0, 14)}...`,
          time: new Date().toISOString(),
        };
      } catch (_) {}
    }

    return saveResult;
  } catch (error) {
    console.warn("[Loop Push] registerPushNotifications failed", error);

    try {
      window.__LOOP_PUSH_LAST_REGISTER__ = {
        ok: false,
        reason: "register-failed",
        error: String(error && error.message ? error.message : error),
        time: new Date().toISOString(),
      };
    } catch (_) {}

    return { ok: false, reason: "register-failed", error };
  } finally {
    registrationInProgress = false;
  }
}

export async function resetPushRegistrationCache() {
  lastRegisteredUid = null;
}

// Revoke provider delivery before auth is cleared, including shared-device logouts.
export async function unregisterPushNotifications() {
  signingOut = true;
  registrationEpoch += 1;
  lastRegisteredUid = null;
  let registration;
  try {registration=JSON.parse(localStorage.getItem('loop_push_registration') || 'null');} catch (_) {}
  const user=getEffectiveUser();
  try {
    if (isNativePlatform(getPlatform())) await FirebaseMessaging.deleteToken();
    else if (messaging) await deleteWebFcmToken(messaging);
  } catch (error) {console.warn('[Loop Push] provider token revoke failed',error);}
  if (registration?.token && registration.uid === user?.uid) {
    await setDoc(doc(db,'users',user.uid,'fcmTokens',safeTokenDocId(registration.token)),{enabled:false,updatedAt:serverTimestamp()},{merge:true});
  }
  try {localStorage.removeItem('loop_push_registration');} catch (_) {}
}

// Read permission only; opening account settings must never prompt the user.
export async function readPushPermission() {
  try {
    if (isNativePlatform(getPlatform())) {
      const permission = await FirebaseMessaging.checkPermissions();
      return permission?.receive || "unknown";
    }
    return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  } catch (_) { return "unknown"; }
}
